import type { DataSession } from "../../../data/core/session";
import { type RouterOutputs, trpc } from "../../../data/core/trpc";
import { getQueryKey } from "@trpc/react-query";
import { NoAuthError } from "@utils/NoAuthError";
import { cdnKeyWireSchema } from "@libs/wire";
import type { z } from "zod";

type CDNKeyOutput = RouterOutputs["user"]["getCDNKey"];

/**
 * Bidirectional drift check pinning `cdnKeyWireSchema` to the trpc router
 * output. See `useLibrary/queryFunctions.ts` for the full rationale.
 */
type _CDNKeyWireSchemaSubset =
  z.infer<typeof cdnKeyWireSchema> extends CDNKeyOutput ? true : never;
type _CDNKeyWireSchemaSuperset =
  CDNKeyOutput extends z.infer<typeof cdnKeyWireSchema> ? true : never;
const _cdnKeyWireSchemaDriftCheck: [
  _CDNKeyWireSchemaSubset,
  _CDNKeyWireSchemaSuperset,
] = [true, true];
void _cdnKeyWireSchemaDriftCheck;

export const USER_GETCDNKEY_QUERY_KEY = getQueryKey(trpc.user.getCDNKey);

export const cdnKeyQueryOptions = (data: DataSession) =>
  data.cache.durableQueryOptions({
    queryKey: USER_GETCDNKEY_QUERY_KEY,
    queryFn: () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("useCDNKey queryFn");
      }
      return data.api.user.getCDNKey.query();
    },
    schema: cdnKeyWireSchema,
    staleTime: 0,
  });

export const getCDNKeyQueryState = (data: DataSession) => {
  if (!data.auth.currentUser()) {
    return undefined;
  }
  const state = data.queryClient.getQueryState<CDNKeyOutput>(
    USER_GETCDNKEY_QUERY_KEY,
  );
  return state;
};

export const fetchCDNKey = (data: DataSession) =>
  data.queryClient.fetchQuery<CDNKeyOutput>(cdnKeyQueryOptions(data));

export const getCDNKeyFromCache = (data: DataSession) => {
  const cdnKey = data.queryClient.getQueryData<CDNKeyOutput>(
    USER_GETCDNKEY_QUERY_KEY,
  );
  return cdnKey;
};
